import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import { ResetPasswordPayload } from '../../core/models/auth.model';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslateModule],
  templateUrl: './forgotPassword.component.html',
  styleUrls: ['./forgotPassword.component.css']
})
export class ForgotPasswordComponent implements OnDestroy {
  step = 1;
  isLoading = false;
  errorMessage: string | null = null;

  email = '';
  code = '';
  newPassword = '';
  confirmPassword = '';
  showNewPassword = false;
  showConfirmPassword = false; 

  countdown = 0;
  private timer: any;

  constructor(
    private authService: AuthService,
    private router: Router,
    private translate: TranslateService
  ) { }

  ngOnDestroy(): void {
    this.clearTimer();
  }
  ngOnInit(): void {
    this.resetForm();
  }

  private resetForm(): void {
    this.email = '';
    this.code = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.showNewPassword = false;
    this.showConfirmPassword = false;
    this.errorMessage = null;
    this.isLoading = false;
    this.countdown = 0;
  }

  toggleShowNewPassword() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleShowConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }



  onSendCode() {
    if (!this.email) {
      this.errorMessage = this.translate.instant('VALIDATION.EMAIL_REQUIRED');
      return;
    }
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(this.email)) {
      this.errorMessage = this.translate.instant('VALIDATION.EMAIL_INVALID');
      return;
    }
    this.isLoading = true;
    this.errorMessage = null;

    this.authService.sendPasswordResetCode(this.email).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.step = 2;
        this.startCountdown(90); //
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err.error?.message || 'Failed to send code. Please try again.';
      }
    });
  }


  onResendCode() {
    if (this.countdown > 0) return;
    this.onSendCode();
  }


  onResetSubmit() {
    if (!this.code || !this.newPassword || !this.confirmPassword) {
      this.errorMessage = this.translate.instant('VALIDATION.FORM_INVALID');
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.errorMessage = this.translate.instant('VALIDATION.PASSWORDS_DO_NOT_MATCH');
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    const payload: ResetPasswordPayload = {
      email: this.email,
      code: this.code,
      new_password: this.newPassword,
      new_password_confirmation: this.confirmPassword
    };

    this.authService.resetPassword(payload).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.status === 'ok') {
          this.step = 3;
          this.clearTimer();
        } else {
          this.errorMessage = res.error || res.message || 'Reset failed.';
        }
      },
      error: (err) => {
        this.isLoading = false;
        const backendError = err.error?.error || err.error?.message || 'Reset request failed.';
        this.errorMessage = backendError;
      }
    });
  }

  private startCountdown(seconds: number) {
    this.countdown = seconds;
    this.clearTimer();
    this.timer = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.clearTimer();
      }
    }, 1000);
  }

  private clearTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}